<!-- src/lib/components/ActivityFeed.svelte -->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { api } from '../api.ts';
  import { onEvent } from '../ws.svelte.ts';
  import type { AppEvent } from '../types.ts';

  let events = $state<AppEvent[]>([]);

  function formatEvent(ev: AppEvent): string {
    switch (ev.event_type) {
      case 'task_queued':
        return `Agent queued: ${(ev.payload as any)?.prompt?.slice(0, 60) ?? ev.agent_id.slice(0, 8)}`;
      case 'task_started':
        return `Agent started: ${ev.agent_id.slice(0, 8)}`;
      case 'task_completed':
        return `Agent completed: ${ev.agent_id.slice(0, 8)}`;
      case 'task_cancelled':
        return `Agent cancelled: ${ev.agent_id.slice(0, 8)}`;
      case 'card_created':
        return `Card created: ${(ev.payload as any)?.title ?? '—'}`;
      case 'card_updated':
        return `Card updated: ${(ev.payload as any)?.title ?? '—'}`;
      case 'agent_assigned':
        return `Agent assigned: ${ev.agent_id.slice(0, 8)}`;
      default:
        return `${ev.event_type}: ${ev.agent_id.slice(0, 8)}`;
    }
  }

  function formatTime(createdAt: string): string {
    const now = Date.now();
    const then = new Date(createdAt).getTime();
    const diffMs = now - then;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  }

  let unsubscribe: (() => void) | null = null;

  onMount(async () => {
    try {
      const recent = await api<AppEvent[]>('/api/v1/events?limit=50');
      events = recent;
    } catch (e) {
      console.warn('ActivityFeed: failed to load events', e);
    }

    unsubscribe = onEvent((ev: AppEvent) => {
      events = [ev, ...events].slice(0, 200);
    });
  });

  onDestroy(() => {
    unsubscribe?.();
  });
</script>

<div class="feed-container">
  <div class="feed-header">
    <span class="feed-title">Activity</span>
    <span class="feed-count">{events.length} events</span>
  </div>
  <div class="feed">
    {#each events as ev (ev.event_id)}
      <div class="event-row">
        <span
          class="event-dot"
          class:agent={ev.event_type.startsWith('task')}
          class:pm={ev.event_type.startsWith('card')}
        ></span>
        <div class="event-content">
          <span class="event-text">{formatEvent(ev)}</span>
          <span class="event-time">{formatTime(ev.created_at)}</span>
        </div>
      </div>
    {/each}
    {#if events.length === 0}
      <p class="empty-hint">No activity yet.</p>
    {/if}
  </div>
</div>

<style>
  .feed-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .feed-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.75rem 0.75rem;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .feed-title {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.3);
    font-weight: 600;
  }

  .feed-count {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.2);
    font-family: 'JetBrains Mono', monospace;
  }

  .feed {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 1.75rem 1.5rem;
  }

  .event-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    min-height: 36px;
    padding: 0.375rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  }

  .event-row:last-child {
    border-bottom: none;
  }

  .event-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    flex-shrink: 0;
    margin-top: 0.3rem;
  }

  .event-dot.agent {
    background: oklch(0.58 0.22 265);
    box-shadow: 0 0 6px oklch(0.55 0.20 265 / 0.50);
  }

  .event-dot.pm {
    background: oklch(0.80 0.26 310);
    box-shadow: 0 0 6px oklch(0.74 0.28 310 / 0.44);
  }

  .event-content {
    display: flex;
    flex: 1;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;
  }

  .event-text {
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  .event-time {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.25);
    flex-shrink: 0;
    font-family: 'JetBrains Mono', monospace;
  }

  .empty-hint {
    color: rgba(255, 255, 255, 0.2);
    font-size: 0.82rem;
    text-align: center;
    margin-top: 2rem;
  }
</style>
